<script setup>
import { computed, onMounted, ref } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import PersianDate from "persian-date"

import store from "../../state/store"
import useOccasions from "../../use/useOccasions"
import useDebouncedFunction from "../../use/useDebouncedFunction"
import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const occasions = computed(() => store.state.occasions)
const occasionTypesWithOccasions = computed(
  () => store.state.occasionTypesWithOccasions
)

const {
  isPublic,
  search,
  occasionTypeId,
  occasionDateType,
  perPage,
  sortField,
  sortDirection,
  getOccasions,
  sortOccasions,
} = useOccasions()
const { handleInput } = useDebouncedFunction(getOccasions)

onMounted(() => {
  getOccasions()
  store.dispatch("getOccasionTypesWithOccasions")
})

const occasionDateTypes = {
  1: "شمسی",
  2: "قمری",
  3: "میلادی",
}

const toPersianDate = (date) => {
  const [year, month, day] = date.split("-")
  const gregorianDate = new Date(year, month - 1, day)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD")
}

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

const currentPage = ref(1)

function getForPage(bvEvent, url) {
  getOccasions(url)
}

/* function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getOccasions(link.url)
} */

const defaultOptions = ref({
  animationData: animationData,
})
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="3">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی عنوان مناسبت ..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="9">
        <b-row class="g-3">
          <b-col sm="3">
            <select
              @change="getOccasions(null)"
              v-model="isPublic"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب وضعیت مناسبت</option>
              <option value="0">خصوصی</option>
              <option value="1">عمومی</option>
            </select>
          </b-col>

          <b-col sm="4">
            <select
              @change="getOccasions(null)"
              v-model="occasionTypeId"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="">انتخاب نوع مناسبت</option>
              <option
                v-for="occasionType in occasionTypesWithOccasions.data"
                :value="occasionType.id"
                :key="occasionType.id"
              >
                {{ occasionType.title }}
              </option>
            </select>
          </b-col>

          <b-col sm="3">
            <select
              @change="getOccasions(null)"
              v-model="occasionDateType"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب نوع تقویم</option>
              <option value="1">شمسی</option>
              <option value="2">قمری</option>
              <option value="3">میلادی</option>
            </select>
          </b-col>

          <b-col sm="2">
            <select
              @change="getOccasions(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="2">2</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="occasionTable">
          <thead class="table-light text-muted">
            <tr>
              <th class="sort" data-sort="occasion_image">تصویر</th>
              <TableHeaderCell
                field="title"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOccasions('title')"
              >
                عنوان برچسب
              </TableHeaderCell>

              <TableHeaderCell
                field="is_public"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOccasions('is_public')"
              >
                وضعیت مناسبت
              </TableHeaderCell>

              <TableHeaderCell
                field="occasion_date_type"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOccasions('occasion_date_type')"
              >
              تقویم
              </TableHeaderCell>

              <TableHeaderCell
                field="occasion_date"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOccasions('occasion_date')"
              >
              تاریخ
              </TableHeaderCell>

              <TableHeaderCell
                field="occasion_type_id"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOccasions('occasion_type_id')"
              >
              نوع مناسبت
              </TableHeaderCell>

              <th class="sort" data-sort="status">یادآوری های ثبت شده</th>
              <TableHeaderCell
                field="created_at"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOccasions('created_at')"
              >
                تاریخ ثبت
              </TableHeaderCell>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>

          <tbody
            class="list"
            v-if="occasions.loading || !occasions.data.length"
          >
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="occasions.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ مناسبتی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(occasion, index) of occasions.data" :key="index">
              <td class="occasion_image">
                <div class="avatar-sm bg-light rounded p-1">
                  <img :src="occasion.image" class="img-fluid d-block" />
                </div>
              </td>
              <td class="occasion_title">
                <h5 class="fs-14 mb-1">{{ occasion.title }}</h5>
                <small class="text-muted mb-0">
                  توسط:
                  <span class="fw-medium">{{ occasion.created_by }}</span>
                </small>
              </td>

              <td class="is_public">
                <span
                  class="badge text-uppercase"
                  :class="{
                    'badge-soft-success': occasion.is_public == 1,
                    'badge-soft-danger': occasion.is_public == 0,
                  }"
                  >{{ occasion.is_public ? "عمومی" : "خصوصی" }}</span
                >
              </td>

              <td class="occasion_date_type">
                {{ occasionDateTypes[occasion.occasion_date_type] }}
              </td>

              <td class="occasion_date">
                {{
                  occasion.occasion_date
                    ? toPersianDate(occasion.occasion_date)
                    : ""
                }}
              </td>
              <td class="occasion_type_title">
                {{ occasion.occasion_type_title }}
              </td>
              <td class="occasion_type_title"></td>

              <td class="date">{{ toPersianDateTime(occasion.created_at) }}</td>

              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Edit"
                  >
                    <RouterLink
                      :to="{
                        name: 'occasion.edit',
                        params: { id: occasion.id },
                      }"
                      class="text-primary d-inline-block edit-item-btn"
                      ><i class="ri-pencil-fill fs-16"></i
                    ></RouterLink>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="overflow-auto mt-2" v-if="occasions.total > occasions.limit">
      {{ occasions.meta }}
        <b-pagination
          v-model="currentPage"
          :total-rows="occasions.total"
          :per-page="occasions.limit"
          @page-click="(bvEvent, page) => getForPage(bvEvent, page)"
          first-number
          last-number
        ></b-pagination>
      </div>
    </div>
  </b-card-body>
</template>

<style>
.page-link {
  border: 1px solid #dee2e6 !important;
}
.overflow-auto .justify-content-start {
  justify-content: end !important;
}

ul.pagination {
  margin-left: 1px !important;
}
</style>
